import { toCSV } from '@/lib/csv'
import type { ComplianceReport, ComplianceReportRoom, ComplianceStatus } from '@/types'

/**
 * Human-readable label for each ComplianceStatus value. Matches what the
 * table renders so the CSV and on-screen report stay in lock-step.
 */
const STATUS_LABELS: Record<ComplianceStatus, string> = {
  compliant: 'Compliant',
  in_progress: 'In progress',
  missed: 'Missed',
  never_cleaned: 'Never cleaned',
  no_target: 'No target',
  not_this_period: 'Not this period',
}

/**
 * Return the canonical label for a status, falling back to the raw enum
 * value for any future backend-added values.
 */
export function statusLabel(status: ComplianceStatus): string {
  return STATUS_LABELS[status] ?? status
}

/**
 * Format the Target column cell. Empty string for the no-target case so
 * spreadsheets render a truly blank cell rather than the literal word "null".
 */
export function formatTarget(room: ComplianceReportRoom): string {
  if (!room.target) return ''
  const unit = room.target.required_cleans === 1 ? 'clean' : 'cleans'
  return `${room.target.required_cleans} ${unit} / ${room.target.period}`
}

/**
 * Format the Progress column cell as "cleans_in_period / required_cleans"
 * (e.g. "1 / 2"). Rooms with no target (or a different period) display the
 * raw cleans count or an empty cell when the backend returned null.
 */
export function formatProgress(room: ComplianceReportRoom): string {
  const have = room.cleans_in_period
  const want = room.target?.required_cleans
  if (have === null || have === undefined) return ''
  if (want === undefined) return String(have)
  return `${have} / ${want}`
}

/**
 * Format last_cleaned_at as an ISO instant. Leaving it as the raw ISO
 * string from the backend keeps the CSV unambiguous for downstream
 * tooling; UI-formatted timestamps belong on the screen, not in the file.
 */
export function formatLastCleaned(room: ComplianceReportRoom): string {
  return room.last_cleaned_at ?? ''
}

/** Flat row shape the CSV writer iterates over. */
export interface ComplianceCsvRow {
  building: string
  room: string
  floor: string
  target: string
  progress: string
  status: string
  lastCleaned: string
}

/**
 * Flatten the hierarchical report into one row per room, with building
 * name repeated on every row. Preserves the backend's building / room
 * ordering.
 */
export function reportToRows(report: ComplianceReport): ComplianceCsvRow[] {
  const rows: ComplianceCsvRow[] = []
  for (const b of report.buildings) {
    for (const r of b.rooms) {
      rows.push({
        building: b.building_name,
        room: r.room_name,
        floor: r.floor ?? '',
        target: formatTarget(r),
        progress: formatProgress(r),
        status: statusLabel(r.status),
        lastCleaned: formatLastCleaned(r),
      })
    }
  }
  return rows
}

/**
 * Render the report as a CSV string. Columns match the on-screen table
 * (Building, Room, Floor, Target, Progress, Status, Last cleaned) and
 * escaping / quoting is delegated to @/lib/csv.toCSV.
 */
export function reportToCSV(report: ComplianceReport): string {
  const rows = reportToRows(report)
  return toCSV<ComplianceCsvRow>(rows, [
    { key: 'building', header: 'Building' },
    { key: 'room', header: 'Room' },
    { key: 'floor', header: 'Floor' },
    { key: 'target', header: 'Target' },
    { key: 'progress', header: 'Progress' },
    { key: 'status', header: 'Status' },
    { key: 'lastCleaned', header: 'Last cleaned' },
  ])
}

/**
 * Build the default filename for a compliance-report CSV export.
 * Uses the report's generated_at timestamp (truncated to date) and the
 * period kind so side-by-side downloads don't collide.
 */
export function csvFilename(report: ComplianceReport): string {
  // generated_at is an ISO instant; strip everything after 'T' for a
  // clean yyyy-mm-dd prefix. Fallback to "unknown" on a malformed value
  // so the file still saves.
  const date = report.generated_at.slice(0, 10) || 'unknown'
  return `compliance-report-${report.period.kind}-${date}.csv`
}
